import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { TimeEntry } from '@altyor-kronos/shared-types';
import { getProjectColor } from '../projectColors';
import {
  HOUR_HEIGHT,
  dateToMinutes,
  resolveOverlaps,
  entryToPosition,
} from './calendarLayout';
import CalendarEntryBlock from './CalendarEntryBlock';
import { colors } from '../../../core/theme/theme';
import type { NonWorkingDay } from '../workingDays';

export interface CalendarDayColumnProps {
  dayIndex: number;
  entries: Array<{ id: string; data: TimeEntry }>;
  projectNames: Map<string, string>;
  designationNames: Map<string, string>;
  projectColors: Map<string, string | undefined>;
  /** Why this day is not a working day, or null for an ordinary weekday. */
  nonWorking: NonWorkingDay | null;
  /**
   * A non-working day starts locked: clicking an empty slot unlocks it
   * instead of opening the create popover. Existing entries stay editable
   * either way — the lock only guards against accidental creation.
   */
  locked: boolean;
  onUnlock: () => void;
  /**
   * Le mois de cette colonne est clôturé pour l'utilisateur courant.
   *
   * À ne pas confondre avec `locked` juste au-dessus : un jour non ouvré se
   * rouvre d'un clic, une clôture mensuelle non. Neutraliser la colonne ICI
   * couvre d'un coup la création, l'édition, le déplacement et le
   * redimensionnement — les quatre passent par ce composant.
   */
  monthClosed: boolean;
  closedTooltip: string;
  isViewingTeammate: boolean;
  readOnlyTooltip: string;
  selectedEntryId: string | null;
  onSelectEntry: (entryId: string) => void;
  onEntryEdit: (entryId: string, x: number, y: number) => void;
  onEmptySlotClick: (dayIndex: number, minute: number, x: number, y: number) => void;
  onEntryPointerDown: (e: React.PointerEvent, entryId: string, startMin: number, endMin: number) => void;
  /** Returns true if a drag just committed — suppresses the spurious click after pointerup. */
  checkDragCommitted: () => boolean;
}

export default function CalendarDayColumn({
  dayIndex,
  entries,
  projectNames,
  designationNames,
  projectColors,
  nonWorking,
  locked,
  onUnlock,
  monthClosed,
  closedTooltip,
  isViewingTeammate,
  readOnlyTooltip,
  selectedEntryId,
  onSelectEntry,
  onEntryEdit,
  onEmptySlotClick,
  onEntryPointerDown,
  checkDragCommitted,
}: CalendarDayColumnProps) {
  // Resolve overlaps for this day's entries
  const overlapInput = entries.map((e) => {
    const st = e.data.startTime.toDate();
    const et = e.data.endTime.toDate();
    return {
      id: e.id,
      startMinutes: dateToMinutes(st),
      endMinutes: dateToMinutes(et),
    };
  });
  const resolved = resolveOverlaps(overlapInput);

  return (
    <Box
      data-testid={`calendar-day-${dayIndex}`}
      sx={{
        position: 'relative',
        height: 24 * HOUR_HEIGHT,
        bgcolor: nonWorking
          ? alpha(colors.lightGray, locked ? 0.55 : 0.3)
          : undefined,
      }}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('[data-entry-id]')) return;
        if (monthClosed) return;
        if (locked) {
          onUnlock();
          return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const minute = Math.floor((y / HOUR_HEIGHT) * 60);
        const snapped = Math.round(minute / 15) * 15;
        onEmptySlotClick(dayIndex, snapped, e.clientX, e.clientY);
      }}
    >
      {/* Entry blocks */}
      {resolved.map((ov) => {
        const entry = entries.find((e) => e.id === ov.id)!;
        const pos = entryToPosition(
          ov.startMinutes,
          ov.endMinutes,
          ov.overlapIndex,
          ov.overlapCount,
        );
        const pColor = getProjectColor(
          entry.data.projectId,
          projectColors.get(entry.data.projectId) ?? undefined,
        );

        return (
          <CalendarEntryBlock
            key={entry.id}
            id={entry.id}
            position={pos}
            projectName={projectNames.get(entry.data.projectId) ?? entry.data.projectId}
            designationName={designationNames.get(entry.data.designationId) ?? ''}
            description={entry.data.description ?? ''}
            durationSeconds={entry.data.durationSeconds}
            color={pColor}
            isTeammate={isViewingTeammate}
            selected={selectedEntryId === entry.id}
            readOnlyTooltip={
              isViewingTeammate ? readOnlyTooltip : monthClosed ? closedTooltip : undefined
            }
            onClick={(e) => {
              e.stopPropagation();
              // Suppress click fired by the browser after a committed drag
              if (checkDragCommitted()) return;
              onSelectEntry(entry.id);
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              // Mois clos : la sélection reste possible (copier vers un mois
              // ouvert est légitime), l'ouverture du formulaire non.
              if (monthClosed) return;
              onEntryEdit(entry.id, e.clientX, e.clientY);
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
              if (monthClosed) return; // ni déplacement ni redimensionnement
              onEntryPointerDown(e, entry.id, ov.startMinutes, ov.endMinutes);
            }}
          />
        );
      })}
    </Box>
  );
}
